import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Settings } from 'lucide-react';
import { useExperiment, useUpdateExperimentConfig } from '../utils/convexHooks';
import { Id } from '../convex/_generated/dataModel';
import { UserProfileButton } from './AuthComponents';
import ThemeToggle from './guided/titration/panels/ThemeToggle';

export default function TeacherExperimentSettings() {
    const { id } = useParams<{ id: Id<"experimentConfigs"> }>();
    const navigate = useNavigate();
    const experiment = useExperiment(id);
    const updateExperiment = useUpdateExperimentConfig();

    const handleGradingChange = async (method: "auto" | "manual") => {
        if (!id) return;
        try {
            await updateExperiment({
                experimentId: id,
                updates: { gradingMethod: method }
            });
        } catch (e) {
            console.error("Failed to update grading method", e);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
                <div className="w-full max-w-[1920px] lg:px-8 xl:px-12 mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/teacher/dashboard')}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-500 dark:text-gray-400"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {experiment ? experiment.title : 'Loading...'}
                                </h1>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Experiment Settings
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <ThemeToggle /> <UserProfileButton />
                        </div>
                    </div>
                </div>
            </header>

            <main className="w-full max-w-[1920px] lg:px-8 xl:px-12 mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {experiment === undefined ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                    </div>
                ) : experiment === null ? (
                    <div className="text-center py-12">
                        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Experiment not found</h3>
                        <p className="text-gray-500 mt-2">The experiment you are looking for does not exist.</p>
                        <button
                            onClick={() => navigate('/teacher/dashboard')}
                            className="mt-6 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                            Return to Dashboard
                        </button>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center max-w-2xl mx-auto mt-12">
                        <div className="flex items-center justify-center w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full mx-auto mb-6">
                            <Settings className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Settings Editor Coming Soon</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg">
                            The full settings editor for editing modes, strictness, and templates after creation is being built.
                        </p>
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 text-left border border-gray-100 dark:border-gray-800">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">Current Configuration Summary</h3>
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Experiment Mode</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{experiment.experimentMode}</dd>
                                </div>
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Class Code</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{experiment.classCode}</dd>
                                </div>
                                <div className="sm:col-span-2">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Grading Method</dt>
                                    <dd className="flex items-center gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-900 dark:text-white">
                                            <input 
                                                type="radio" 
                                                checked={experiment.gradingMethod === 'auto' || !experiment.gradingMethod} 
                                                onChange={() => handleGradingChange('auto')}
                                                className="text-indigo-600 focus:ring-indigo-500" 
                                            />
                                            Auto Grading (AI)
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-900 dark:text-white">
                                            <input 
                                                type="radio" 
                                                checked={experiment.gradingMethod === 'manual'} 
                                                onChange={() => handleGradingChange('manual')}
                                                className="text-indigo-600 focus:ring-indigo-500" 
                                            />
                                            Manual Grading
                                        </label>
                                    </dd>
                                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                        {experiment.gradingMethod === 'manual' 
                                            ? "Students will submit their work. You must review and enter a grade manually." 
                                            : "The system will automatically grade lab data and use AI to evaluate discussion answers."}
                                    </p>
                                </div>
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Pre-Lab Status</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{experiment.preLab.enabled ? 'Enabled' : 'Disabled'}</dd>
                                </div>
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Post-Lab Status</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{experiment.postLab.enabled ? 'Enabled' : 'Disabled'}</dd>
                                </div>
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Retakes Allowed</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{experiment.maxRetakes === 'unlimited' ? '∞' : experiment.maxRetakes ?? 'Unlimited'}</dd>
                                </div>
                            </dl>
                        </div>
                        <button
                            onClick={() => navigate('/teacher/dashboard')}
                            className="mt-8 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
}
